import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { Box, Divider, IconButton, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography, Stack, Chip, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { request } from '../../Utils/request';
import BottomModal from '../../Components/BottomModel';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { RootState } from '../../store/rootReducers';

type Coordinate = { lat: number; lng: number };

type Place = {
  _id: string;
  name: string;
  location: { lat: number; lng: number; name?: string };
  files?: { fileKey: string; fileType: string; featured?: boolean; fileURL?: string }[];
  description?: string;
  categories?: string[];
};

const APP_BAR_HEIGHT = 56;
const containerStyle = { width: '100%', height: `calc(100vh - ${APP_BAR_HEIGHT}px)` } as const;

const getLatLng = (place: Place): Coordinate => ({
  lat: place.location.lat,
  lng: place.location.lng,
});

interface VisitedPlacesProps {
  places?: Place[];
  onBack?: () => void;
}

const VisitedPlaces: React.FC<VisitedPlacesProps> = ({ places, onBack }) => {
  const navigate = useNavigate();
  const { id: routeUserId } = useParams<{ id?: string }>();
  const currentUserId = useSelector((state: RootState) => state.user?.user?.id || state.user?.user?._id);
  const [data, setData] = useState<Place[]>(places || []);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  });

  const listForView = useMemo(() => data, [data]);
  const center: Coordinate = useMemo(() => {
    if (!listForView.length) return { lat: 20.5937, lng: 78.9629 };
    return getLatLng(listForView[0]);
  }, [listForView]);
  const shouldFitAll = listForView.length >= 2;

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const isOtherUser = Boolean(routeUserId) && routeUserId !== currentUserId;
        const endpoint = isOtherUser
          ? `/profile/${routeUserId}/visited-places?page=1&limit=10`
          : `/profile/visited-places`;
        const res = await request<{ status: boolean; data: { visitedPlaces: Place[] } }>(endpoint, {
          method: 'GET',
          authorization: true,
        });
        if (isMounted && Array.isArray(res?.data?.visitedPlaces)) {
          setData(res.data.visitedPlaces);
          if (res.data.visitedPlaces[0]?._id) {
            setActiveId(res.data.visitedPlaces[0]._id);
          }
        } else if (isMounted) {
          setData([]);
        }
      } catch (e: any) {
        if (isMounted) {
          setError(e?.message || 'Failed to load visited places');
          setData([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    // Only fetch if places prop is not provided
    if (!places) {
      load();
    } else {
      setData(places);
      if (places[0]?._id) {
        setActiveId(places[0]._id);
      }
      setLoading(false);
    }
    return () => {
      isMounted = false;
    };
  }, [places, routeUserId, currentUserId]);

  // Fit bounds when data changes and map is ready
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const list = listForView;
    if (list.length >= 2) {
      const bounds = new google.maps.LatLngBounds();
      list.forEach((p) => bounds.extend(getLatLng(p)));
      mapRef.current.fitBounds(bounds, 48);
    } else if (list.length === 1) {
      const only = getLatLng(list[0]);
      mapRef.current.setCenter(only);
      mapRef.current.setZoom(14);
    }
  }, [isLoaded, listForView]);

  const handleSelect = (place: Place) => {
    setActiveId(place._id);
    const position = getLatLng(place);
    if (mapRef.current) {
      mapRef.current.panTo(position);
      mapRef.current.setZoom(14);
    }
    navigate(`/places/${place._id}`);
  };

  if (loadError) return <Typography>Failed to load map</Typography>;
  if (!isLoaded) return <Typography>Loading map...</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
    <TopNavBar title='Visited place' canGoBack/>
      <Divider />

      <Box sx={{ position: 'relative' }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={shouldFitAll ? undefined : center}
          zoom={shouldFitAll ? undefined : 10}
          options={{ disableDefaultUI: true, zoomControl: true }}
          onLoad={(m) => {
            mapRef.current = m;
            // Initial fit
            const list = listForView;
            if (list.length >= 2) {
              const bounds = new google.maps.LatLngBounds();
              list.forEach((p) => bounds.extend(getLatLng(p)));
              m.fitBounds(bounds, 48);
            } else if (list.length === 1) {
              const only = getLatLng(list[0]);
              m.setCenter(only);
              m.setZoom(14);
            }
          }}
        >
          {data.map((p) => (
            <Marker
              key={p._id}
              position={getLatLng(p)}
              onClick={() => handleSelect(p)}
              icon={activeId === p._id ? undefined : undefined}
            />
          ))}
        </GoogleMap>

        <Button
          variant="contained"
          size="small"
          onClick={() => setDrawerOpen(true)}
          sx={{ position: 'absolute', bottom: 16, left: 16, borderRadius: 999, textTransform: 'none' }}
        >
          Places
        </Button>
      </Box>

      <BottomModal
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Visited places"
        maxHeight="70vh"
        minHeight="240px"
        contentProps={{ sx: { p: 0 } }}
        showDragIndicator
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2">Loading visited places...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          ) : data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No visited places found
              </Typography>
            </Box>
          ) : (
            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {data.map((p) => {
                const isActive = activeId === p._id;
                const featuredFile = p.files?.find((f) => f.featured) || p.files?.[0];
                const thumbSrc = featuredFile?.fileURL || (featuredFile?.fileKey ? `/api/files/${featuredFile.fileKey}` : undefined);
                const categories = p.categories || [];
                return (
                  <ListItem
                    key={p._id}
                    alignItems="flex-start"
                    onClick={() => handleSelect(p)}
                    sx={{ cursor: 'pointer', px: 2 }}
                  >
                    <ListItemAvatar>
                      <Avatar variant="rounded" src={thumbSrc} sx={{ width: 64, height: 64 }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mx: 2 }}>
                          <Typography fontWeight={700}>{p.name}</Typography>
                        </Stack>
                      }
                      secondary={
                        categories.length > 0 ? (
                          <Stack spacing={0.5} mt={0.5} mx={2}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {categories.map((category, index) => (
                                <React.Fragment key={category}>
                                  <Typography variant="body2" color="text.secondary">
                                    {category}
                                  </Typography>
                                  {index < categories.length - 1 && (
                                    <Typography variant="body2" color="text.secondary">
                                      •
                                    </Typography>
                                  )}
                                </React.Fragment>
                              ))}
                            </Stack>
                            {p.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {p.description.length > 100 ? `${p.description.substring(0, 100)}...` : p.description}
                              </Typography>
                            )}
                          </Stack>
                        ) : p.description ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mx: 2 }}>
                            {p.description.length > 100 ? `${p.description.substring(0, 100)}...` : p.description}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </BottomModal>
    </Box>
  );
};

export default VisitedPlaces;


